import { Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useDrop } from "react-dnd";
import { useSelector } from "react-redux";
import type { DimensionDescriptor } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import {
  selectStatementAxes,
  selectStatementDataColumns,
  selectStatementLines,
} from "../../../../store/selectors/fieldsStateSelectors.ts";
import { createFinancialStatementAction } from "../../../../store/utils/storeUtils.ts";
import AddField from "../../../viewer/common/fields/area-fields/AddField.tsx";
import { axesAreaItemTypes, MAX_AXES, resolveAxesDrop, resolveFieldForAxes } from "../utils/statementColumns.ts";
import type { StatementFieldDragItem } from "../utils/statementRowsArea.ts";
import AddAxisMenu from "./AddAxisMenu.tsx";
import AxisCard from "./AxisCard.tsx";
import AxisOptionsPopover from "./AxisOptionsPopover.tsx";
import useAxisOutcomeHandler from "./useAxisOutcomeHandler.ts";

interface AxisOptionsTarget {
  guid: string;
  anchorEl: HTMLElement;
}

interface Props {
  readonlyMode: boolean;
}

const AxesArea = ({ readonlyMode }: Props) => {
  const dispatch = useAppDispatch();
  const axes = useSelector(selectStatementAxes);
  const lines = useSelector(selectStatementLines);
  const dataColumns = useSelector(selectStatementDataColumns);
  const [optionsTarget, setOptionsTarget] = useState<AxisOptionsTarget>();
  const [addAxisAnchorEl, setAddAxisAnchorEl] = useState<HTMLElement>();
  const handleAxisOutcome = useAxisOutcomeHandler();

  const handleDrop = useCallback(
    (item: StatementFieldDragItem) => handleAxisOutcome(resolveAxesDrop(item, { axes, lines, dataColumns })),
    [axes, dataColumns, handleAxisOutcome, lines]
  );

  const handleShowOptions = useCallback(
    (guid: string, anchorEl: HTMLElement) => setOptionsTarget({ guid, anchorEl }),
    []
  );

  const handleRemove = useCallback(
    (guid: string) => dispatch(createFinancialStatementAction("removeAxis", guid)),
    [dispatch]
  );

  const [{ isOver }, drop] = useDrop<StatementFieldDragItem, void, { isOver: boolean }>(
    () => ({
      accept: axesAreaItemTypes,
      canDrop: () => !readonlyMode,
      drop: handleDrop,
      collect: (monitor) => ({ isOver: monitor.isOver() && monitor.canDrop() }),
    }),
    [handleDrop, readonlyMode]
  );
  const handleAreaRef = useCallback(
    (element: HTMLDivElement | null) => {
      drop(element);
    },
    [drop]
  );

  const handlePickDimension = (dimension: DimensionDescriptor) => {
    setAddAxisAnchorEl(undefined);
    handleAxisOutcome(resolveFieldForAxes({ kind: "dimension", dimension }, { axes, lines, dataColumns }));
  };

  const optionsIndex = optionsTarget ? axes.findIndex((axis) => axis.field.config.guid === optionsTarget.guid) : -1;
  const optionsAxis = axes[optionsIndex];

  return (
    <Stack sx={{ gap: ".5rem" }}>
      <Typography variant="subtitle2" color="textPrimary">
        Columns
      </Typography>
      <Stack
        ref={handleAreaRef}
        sx={(theme) => ({
          gap: "5px",
          minHeight: readonlyMode ? "40px" : "90px",
          p: ".5rem",
          bgcolor: "background.grey",
          border: `1px solid ${isOver ? theme.palette.primary.main : theme.palette.divider}`,
          borderRadius: "4px",
        })}
      >
        {axes.map((axis) => (
          <AxisCard
            key={axis.field.config.guid}
            axis={axis}
            optionsOpen={axis.field.config.guid === optionsTarget?.guid}
            readonly={readonlyMode}
            onShowOptions={handleShowOptions}
            onRemove={handleRemove}
          />
        ))}
        {!readonlyMode && axes.length < MAX_AXES && (
          <AddField disabled={false} onAddField={(event) => setAddAxisAnchorEl(event.currentTarget)}>
            Add column...
          </AddField>
        )}
      </Stack>
      {addAxisAnchorEl && (
        <AddAxisMenu
          anchorEl={addAxisAnchorEl}
          onPick={handlePickDimension}
          onClose={() => setAddAxisAnchorEl(undefined)}
        />
      )}
      {optionsTarget && optionsAxis && (
        <AxisOptionsPopover
          axis={optionsAxis}
          canMakeOuter={optionsIndex > 0}
          anchorEl={optionsTarget.anchorEl}
          readonly={readonlyMode}
          onClose={() => setOptionsTarget(undefined)}
        />
      )}
    </Stack>
  );
};

export default AxesArea;
